import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../../../lib/intersectionObserverPolyfill'; 

import { confService, itemsPerPage } from '../../../Services'

import {ReactComponent as Loading} from '../../../images/icons/General/loading-01.svg';
import {ReactComponent as Plus} from '../../../images/icons/General/plus.svg';
import {ReactComponent as Sunset} from '../../../images/icons/Weather/sunset.svg';
import {ReactComponent as Sunrise} from '../../../images/icons/Weather/sunrise.svg';
import {ReactComponent as Edit} from '../../../images/icons/General/edit-01.svg';

export const Pages = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [info, setInfo] = useState([]);
    const [number, setNumber] = useState(0);
    const [statusLoading, setStatusLoading] = useState(0);
    const [sortField, setSortField] = useState('Name');
    const [sortOrder, setSortOrder] = useState(false);
    const [page, setPage] = useState(-1);
    const [hasMore, setHasMore] = useState(true);
    const loaderRef = useRef(null);
    const tableRef = useRef(null);
    const initialPageRef = useRef(Math.max(parseInt(searchParams.get('page')) || 1, 1) - 1);
    const firstLoadRef = useRef(true);
    const scrollToIndexRef = useRef(null);

    const resetValues = () => {
        setInfo([]);
        setPage(-1);
        setHasMore(true);
        setNumber(0);
    }

    const sort = (e) => {
        if (e.target.id === sortField)
            setSortOrder(!sortOrder)
        else 
            setSortField(e.target.id);

        resetValues();
    }

    const setStatus = async (id, statusValue) => {
        setStatusLoading(id);
        const res = await confService.pageStatus({
            id: id,
            status: statusValue ? 1 : 0
        });

        if (res.result === 'OK')
            setInfo(prev => prev.map(item => item.id === id ? {...item, status: statusValue ? 1 : 0} : item));

        setStatusLoading(0);
    }

    const fetchData = useCallback(async (pageNum, pagesCount, sortFieldValue, sortOrderValue) => {
        const res = await confService.getPages(pageNum, sortFieldValue, sortOrderValue, pagesCount);
        const list = res?.list || [];
        setInfo(prev => [...prev, ...list]);

        if (pagesCount > 1)
            scrollToIndexRef.current = itemsPerPage * (pagesCount - 1);

        if (res?.number)
            setNumber(res.number);

        if (list.length < itemsPerPage * pagesCount)
            setHasMore(false);
    }, []);
    
    useEffect(() => {
        const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage((prev) => prev === -1 && firstLoadRef.current ? initialPageRef.current : prev + 1);
            }
        },
        { threshold: 0.1 }
        );

        const loader = loaderRef.current;
        if (loader) observer.observe(loader);

        return () => {
            if (loader) observer.unobserve(loader);
        };
    }, [hasMore]);

    useEffect(() => {
        if (page < 0) return;
        // on the first load fetch all pages up to the one from the url in one request
        const pagesCount = firstLoadRef.current ? page + 1 : 1;
        firstLoadRef.current = false;
        fetchData(page + 1 - pagesCount, pagesCount, sortField, sortOrder);
    }, [fetchData, page, sortField, sortOrder]);

    useEffect(() => {
        if (page < 0) return;
        const urlPage = page > 0 ? String(page + 1) : null;
        if (searchParams.get('page') === urlPage) return;
        const newParams = new URLSearchParams(searchParams);
        if (urlPage)
            newParams.set('page', urlPage);
        else
            newParams.delete('page');
        setSearchParams(newParams, { replace: true });
    }, [page, searchParams, setSearchParams]);

    useEffect(() => {
        const index = scrollToIndexRef.current;
        if (index === null) return;
        scrollToIndexRef.current = null;
        const row = tableRef.current?.tBodies[0]?.rows[index];
        if (row)
            row.scrollIntoView({ block: 'start' });
    }, [info]);

    return (
        <>
            <h1>Pages</h1>
            {info ? <>
                <div className="bordered-content">
                    <div className="header">
                        <h4>{number} item{number === 1 ? '' : 's'}</h4>
                        <div className="buttons">
                            <Link to="/configuration/pages/add">
                                <button className="primary" type="button"><Plus />Add page</button>
                            </Link>
                        </div>
                    </div>
                    <table id="history-table" ref={tableRef}>
                        <thead>
                            <tr>
                                <th style={{width: '80%'}} className={`sortable ${sortField === 'Name' ? (sortOrder ? 'down' : 'up') : ''}`} id="Name" onClick={sort}>Title</th>
                                <th style={{width: '10%'}} className="light">Status</th>
                                <th style={{width: '10%'}} className="light">Edit</th>  
                            </tr>
                        </thead>
                        <tbody>
                            {info.length ? info.map((item, key) => {
                                return (
                                    <tr key={item.id} className={!item.status ? 'disabled' : ''}>
                                        <td><Link to={`/${item.slug}`}>{item.name}</Link></td>
                                        <td className="desktop">
                                            {statusLoading === item.id ?
                                                <Loading className="btn-loader" /> :
                                                (item.status ?
                                                    <Sunset className="pointer" onClick={() => statusLoading === item.id ? {} : setStatus(item.id, !item.status)} /> :
                                                    <Sunrise className="pointer" onClick={() => statusLoading === item.id ? {} : setStatus(item.id, !item.status)} />
                                                )
                                            }
                                        </td>
                                        <td className="desktop last"><Link to={`/configuration/pages/${item.id}`}><Edit className="pointer" /></Link></td>
                                        <td className="mobile">
                                            <label className="label link" onClick={() => statusLoading === item.id ? {} : setStatus(item.id, !item.status)}>{statusLoading === item.id ? <Loading className="btn-loader" /> : ''}{item.status ? 'Deactivate' : 'Activate'}</label>
                                            <Link to={`/configuration/pages/${item.id}`}><label className="label link">Edit</label></Link>
                                        </td>
                                    </tr>
                                )}
                            ) : (hasMore ? <></> : <tr><td colSpan="9" className="load-more">No pages found</td></tr>)}
                            {hasMore && <tr ref={loaderRef}><td colSpan="9" className="load-more">Loading more...</td></tr>}
                        </tbody>
                    </table>
                </div>
            </> : <p className="empty">No pages found</p>}
        </>
    );
};

export default Pages;